// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useListDevices, useListTemplates, useListContactGroups } from "../lib/api-client-react";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useLang } from "../lib/lang";

const API = import.meta.env.VITE_API_URL ?? "";

interface Schedule {
  id: number;
  name: string;
  deviceId: number | null;
  deviceName: string | null;
  templateId: number | null;
  templateName: string | null;
  groupId: number | null;
  groupName: string | null;
  message: string | null;
  scheduledAt: string;
  status: "pending" | "sent" | "failed";
  errorMessage: string | null;
  recurrence: string;
  createdAt: string;
}

async function fetchSchedules(): Promise<Schedule[]> {
  const r = await fetch(`${API}/api/schedules`);
  return r.json();
}

function toDatetimeLocal(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const statusStyle = (status: string) => {
  const map: Record<string, string> = {
    pending: "bg-[#fff3cd] text-[#856404]",
    sent: "bg-[#d4edda] text-[#155724]",
    failed: "bg-[#f8d7da] text-[#721c24]",
  };
  return map[status] ?? "bg-[#e9ecef] text-[#525f7f]";
};

export function ScheduledMessages() {
  const { t } = useLang();
  const d = t.scheduled;
  const qc = useQueryClient();
  const { data: schedules = [], isLoading } = useQuery({ queryKey: ["schedules"], queryFn: fetchSchedules });
  const { data: devices } = useListDevices();
  const { data: templates } = useListTemplates();
  const { data: groups } = useListContactGroups();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [sName, setSName] = useState("");
  const [sDevice, setSDevice] = useState("");
  const [sTemplate, setSTemplate] = useState("");
  const [sGroup, setSGroup] = useState("");
  const [sMessage, setSMessage] = useState("");
  const [sDate, setSDate] = useState("");
  const [sRecurrence, setSRecurrence] = useState("none");

  const openCreate = () => {
    setEditingId(null);
    setSName(""); setSDevice(""); setSTemplate(""); setSGroup(""); setSMessage(""); setSDate(""); setSRecurrence("none");
    setShowForm(true);
  };

  const openEdit = (s: Schedule) => {
    setEditingId(s.id);
    setSName(s.name);
    setSDevice(s.deviceId ? String(s.deviceId) : "");
    setSTemplate(s.templateId ? String(s.templateId) : "");
    setSGroup(s.groupId ? String(s.groupId) : "");
    setSMessage(s.message ?? "");
    setSDate(toDatetimeLocal(s.scheduledAt));
    setSRecurrence(s.recurrence ?? "none");
    setShowForm(true);
  };

  const create = useMutation({
    mutationFn: async (body: object) => {
      const r = await fetch(`${API}/api/schedules`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: object }) => {
      const r = await fetch(`${API}/api/schedules/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  });

  const del = useMutation({
    mutationFn: async (id: number) => {
      await fetch(`${API}/api/schedules/${id}`, { method: "DELETE" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = {
      name: sName,
      deviceId: sDevice ? Number(sDevice) : null,
      templateId: sTemplate ? Number(sTemplate) : null,
      groupId: sGroup ? Number(sGroup) : null,
      message: sMessage.trim() || null,
      scheduledAt: new Date(sDate).toISOString(),
      recurrence: sRecurrence,
    };
    if (editingId !== null) {
      await update.mutateAsync({ id: editingId, body });
    } else {
      await create.mutateAsync(body);
    }
    setShowForm(false);
    setEditingId(null);
    setSName(""); setSDevice(""); setSTemplate(""); setSGroup(""); setSDate(""); setSRecurrence("none");
  };

  const total = schedules.length;
  const pending = schedules.filter(s => s.status === "pending").length;
  const executed = schedules.filter(s => s.status === "sent").length;
  const failed = schedules.filter(s => s.status === "failed").length;
  const isBusy = create.isPending || update.isPending;

  return (
    <div>
      <PageHeader
        title={d.title}
        actions={[{ label: d.newSchedule, icon: "fas fa-calendar-plus", onClick: openCreate }]}
      />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-4 gap-4">
          <StatCard label={d.totalSchedules} value={total} iconClass="fas fa-calendar-alt" gradient="primary" />
          <StatCard label={d.pendingSchedules} value={pending} iconClass="fas fa-clock" gradient="warning" />
          <StatCard label={d.sentSchedules} value={executed} iconClass="fas fa-rocket" gradient="success" />
          <StatCard label={t.common.failed} value={failed} iconClass="fas fa-calendar-times" gradient="danger" />
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <h5 className="font-semibold text-[#32325d]">
                  {editingId !== null ? d.editSchedule : d.newSchedule}
                </h5>
                <button onClick={() => setShowForm(false)} className="text-[#8898aa] hover:text-[#32325d]"><i className="fas fa-times" /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.scheduleName}</label>
                  <input value={sName} onChange={e => setSName(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder="Morning Newsletter" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.device}</label>
                  <select value={sDevice} onChange={e => setSDevice(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" required>
                    <option value="">{d.selectDevice}</option>
                    {devices?.map(dev => <option key={dev.id} value={dev.id}>{dev.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.template}</label>
                  <select value={sTemplate} onChange={e => setSTemplate(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
                    <option value="">{d.selectTemplate}</option>
                    {templates?.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
                  </select>
                  <p className="text-[11px] text-[#8898aa] mt-1">{d.templateOrMessage}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.messageText}</label>
                  <textarea
                    value={sMessage}
                    onChange={e => setSMessage(e.target.value)}
                    rows={3}
                    className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none"
                    placeholder={d.messageHint}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.group}</label>
                  <select value={sGroup} onChange={e => setSGroup(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
                    <option value="">{d.selectGroup}</option>
                    {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.scheduledAt}</label>
                  <input type="datetime-local" value={sDate} onChange={e => setSDate(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.recurrence}</label>
                  <select value={sRecurrence} onChange={e => setSRecurrence(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
                    {Object.entries(d.recurrenceOptions).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">{t.common.cancel}</button>
                  <button type="submit" disabled={isBusy} className="px-4 py-2 text-sm gradient-primary text-white rounded-lg shadow-sm disabled:opacity-50">
                    {isBusy ? t.common.saving : (editingId !== null ? d.saveChanges : d.newSchedule)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
          <div className="px-5 py-4 border-b border-[#e9ecef]">
            <h4 className="text-sm font-semibold text-[#32325d]">{d.title}</h4>
          </div>
          {isLoading ? (
            <div className="p-12 text-center"><i className="fas fa-spinner fa-spin text-2xl text-[#1171ef]" /></div>
          ) : schedules.length === 0 ? (
            <div className="p-12 text-center">
              <i className="fas fa-calendar-alt text-4xl text-[#cad1d7] mb-3 block" />
              <p className="text-[#8898aa] text-sm mb-1">{d.noSchedules}</p>
              <p className="text-[#adb5bd] text-xs mb-4">{d.noSchedulesHint}</p>
              <button onClick={openCreate} className="gradient-primary text-white px-5 py-2 rounded-lg text-sm shadow-sm">
                <i className="fas fa-plus me-2" />{d.newSchedule}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.scheduleName}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.device}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.scheduledAt}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{d.recurrence}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{t.common.status}</th>
                    <th className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{t.common.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(s => (
                    <tr key={s.id} className="border-b border-[#f6f9fc] hover:bg-[#f8f9fe]">
                      <td className="px-5 py-3 text-sm font-medium text-[#32325d]">{s.name}</td>
                      <td className="px-5 py-3 text-sm text-[#525f7f]">{s.deviceName ?? "—"}</td>
                      <td className="px-5 py-3 text-sm text-[#525f7f]">{new Date(s.scheduledAt).toLocaleString()}</td>
                      <td className="px-5 py-3 text-xs text-[#8898aa]">
                        {s.recurrence && s.recurrence !== "none"
                          ? <span className="flex items-center gap-1"><i className="fas fa-redo text-[10px] text-[#1171ef]" />{d.recurrenceOptions[s.recurrence as keyof typeof d.recurrenceOptions] ?? s.recurrence}</span>
                          : "—"}
                      </td>
                      <td className="px-5 py-3">
                        <div>
                          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${statusStyle(s.status)}`}>
                            {s.status === "pending" && <i className="fas fa-clock me-1 text-[10px]" />}
                            {s.status === "sent" && <i className="fas fa-check me-1 text-[10px]" />}
                            {s.status === "failed" && <i className="fas fa-times me-1 text-[10px]" />}
                            {d.status[s.status as keyof typeof d.status] ?? s.status}
                          </span>
                          {s.errorMessage && (
                            <p className="text-[11px] text-[#f5365c] mt-1 max-w-[180px] truncate" title={s.errorMessage}>
                              {s.errorMessage}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {s.status === "pending" && (
                            <button
                              onClick={() => openEdit(s)}
                              className="flex items-center gap-1 text-xs text-[#1171ef] hover:underline"
                            >
                              <i className="fas fa-pen" />{d.editSchedule}
                            </button>
                          )}
                          <button
                            onClick={() => del.mutate(s.id)}
                            className="flex items-center gap-1 text-xs text-[#f5365c] hover:underline"
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
